'use client'
import { useRouter } from 'next/navigation'
import { useState, useCallback, useEffect } from 'react'

import ShareBottomSheet from '@/components/detail/components/shared/parkinglotShare/view'
import FunctionSectionView from '@/components/map/components/summary/components/functionSection/view'
import InfoSectionView from '@/components/map/components/summary/components/infoSection/view'
import TitleSectionView from '@/components/map/components/summary/components/titleSection/view'

import exploreEvents from '@/utils/gaEvents/exploreEvents'
import paymentEvents from '@/utils/gaEvents/paymentEvents'

import type { SharedParkinglotDetail } from '@/api/server/detail/dto/detailDto'
import Shared from '@/shared'
import NavigationView from '@/shared/components/navigation/view'
import SectionBar from '@/shared/components/SectionBar'
import { Text, Tint } from '@/shared/components/typography'
import useHash from '@/shared/hooks/useHash'

import { PARKINGLOT_TYPE, SHEET_STATE_TYPE } from '@/enums/detail'
import { HASH_KEY_TYPE } from '@/enums/map'

export interface SharedParkinglotSummaryProps {
  className?: React.ComponentProps<'div'>['className']
  pin: SharedParkinglotDetail
  isRoadViewOpen: boolean
  setIsRoadViewOpen: React.Dispatch<React.SetStateAction<boolean>>
  sheetType: SHEET_STATE_TYPE
}

export default function SharedParkinglotSummary(props: SharedParkinglotSummaryProps) {
  const { className, pin, setIsRoadViewOpen, sheetType } = props
  const router = useRouter()
  const { updateHash, getHashByKey } = useHash()
  const [isNavigationOpen, setIsNavigationOpen] = useState(false)
  const [isShareOpen, setIsShareOpen] = useState(false)
  const [isCopySuccess, setIsCopySuccess] = useState(false)
  const [isCopyFail, setIsCopyFail] = useState(false)
  const setIsPurchase = Shared.States.useIsPurchase((state) => state.setIsPurchase)

  const customClassName = className ? className : 'bottom-0 left-0 flex w-full'

  const copyAddress = useCallback(async () => {
    try {
      await Shared.Utils.handleCopyClipBoard(pin.basic.newAddress || pin.basic.address)
      setIsCopySuccess(true)
    } catch (err) {
      setIsCopyFail(true)
    }
  }, [pin.basic.newAddress, pin.basic.address])

  useEffect(() => {
    if (pin.type === PARKINGLOT_TYPE.SHARE && sheetType === SHEET_STATE_TYPE.VISIBLE) {
      exploreEvents.shared.onLoadParkinglotDetail(pin.seq, pin.basic.latitude, pin.basic.longitude)
    }
  }, [pin, sheetType])

  const onClickPurchaseStart = () => {
    setIsPurchase(true)
    paymentEvents.shared.paymentStart(Number(pin.seq))
  }

  const onClickSwipeButton = () => {
    const pathname = window.location.pathname
    const searchParams = window.location.search
    const hash = updateHash({
      sheet: sheetType === SHEET_STATE_TYPE.VISIBLE ? SHEET_STATE_TYPE.HIDDEN : SHEET_STATE_TYPE.VISIBLE
    })
    router.push(`${pathname}${searchParams}${hash}`)
  }

  const isFullSheet = getHashByKey(HASH_KEY_TYPE.SHEET) === SHEET_STATE_TYPE.VISIBLE

  return (
    <div className={`${customClassName}`}>
      <div className={`relative mx-[0] mb-[0] mt-[0] w-full`}>
        <div
          className="absolute left-1/2 flex -translate-x-1/2 cursor-pointer justify-center px-2 py-[10px]"
          onClick={onClickSwipeButton}
        >
          <div className="h-1 w-10 rounded-[26px] bg-020"></div>
        </div>
        <div className={`flex w-full flex-col pt-[24px]`}>
          <TitleSectionView
            parkinglotName={pin.basic.name}
            parkinglotType={pin.type}
            isPartner={false}
            category={pin.basic.category}
            qty={pin.basic.qty}
          />
          <div className="overflow-hidden">
            <InfoSectionView
              parkinglotType={pin.type}
              isCopySuccess={isCopySuccess}
              setIsCopySuccess={setIsCopySuccess}
              isCopyFail={isCopyFail}
              setIsCopyFail={setIsCopyFail}
              copyAddress={copyAddress}
              address={pin.basic.newAddress || pin.basic.address}
              calcPrice={pin.basic.calcPrices && pin.basic.calcPrices['60']}
              operationTime={pin.basic.operationTime}
              isFullSheet={isFullSheet}
            />

            <div className="w-full bg-010 px-[16px] pt-[20px]">
              <div
                className="flex h-[56px] cursor-pointer flex-col items-center justify-center rounded-md bg-main-050"
                onClick={onClickPurchaseStart}
              >
                <Text level="text2" weight="regular" className="text-text-010">
                  주차 시작하기
                </Text>
                <Tint level="tint1" weight="regular" className="text-text-010">
                  결제 직후 바로 주차가 시작 됩니다.
                </Tint>
              </div>
            </div>

            <SectionBar className="mt-[20px]" border="border-t-[1px]" />
            <FunctionSectionView
              pin={pin}
              setIsShareOpen={setIsShareOpen}
              setIsNavigationOpen={setIsNavigationOpen}
              setIsRoadViewOpen={setIsRoadViewOpen}
            />
            <SectionBar className="mb-[24px]" border="border-t-[10px]" />
          </div>
        </div>

        <NavigationView
          targetId={sheetType === SHEET_STATE_TYPE.VISIBLE ? '#layer2' : '#layer1'}
          isOpen={isNavigationOpen}
          onClose={() => setIsNavigationOpen(false)}
          parkinglotName={pin.basic.name}
          latitude={pin.basic.latitude}
          longitude={pin.basic.longitude}
        />
        <ShareBottomSheet
          targetId={sheetType === SHEET_STATE_TYPE.VISIBLE ? '#layer2' : '#layer1'}
          isOpen={isShareOpen}
          onClose={() => setIsShareOpen(false)}
          type={pin.type}
          seq={pin.seq}
          parkinglotName={pin.basic.name}
          parkinglotAddress={pin.basic.newAddress || pin.basic.address}
        />
      </div>
    </div>
  )
}
